import React, { FC } from 'react'

import { Trans, Plural } from '@lingui/macro'
import { Link } from 'react-router-dom'

import { useSharedWithMeQuery } from 'front-end/src/components/dashboard/shared-with-me/SharedWithMe.codegen'
import { TooltipHelpQuestion } from 'front-end/src/components/common/tooltip/TooltipHelp'

import { PageContentWrapper } from '../../../../components/common/page/Page'
import {
  resourceCardCoverImageSrcSet,
  ResourceCard
} from '../../../workspace-page/resource-card/ResourceCard'
import { staticLinks } from '../../../../../../shared/linkBuilder'
import CardGrid from '../../../../components/common/card-grid/CardGrid'
import Loader from '../../../../components/common/loader/Loader'

import './SharedWithMe.less'
import { dayjsFromNow } from 'shared/dayjs'
import { PathwayModelAsStudent } from 'front-end/src/graphql/private/PrivateSchemaTypes'

export const SharedWithMe: FC<{
  topicEnrolmentLimit?: number | null
}> = ({ topicEnrolmentLimit }) => {
  const qr = useSharedWithMeQuery({ variables: { topicEnrolmentLimit } })

  const { data } = qr
  if (!data) {
    return <Loader />
  }

  const { assignedEnrolments, me, assignedEnrolmentsCount } = data

  if (me.assignedPathways.length === 0 && assignedEnrolments.length === 0) {
    return null
  }
  type DateAndElementTuples = Array<[string | null | undefined, JSX.Element]>
  const topicAssignmentCards: DateAndElementTuples = assignedEnrolments.map(
    (assignment) => {
      const { topic } = assignment
      const { currentTopicVersion } = topic

      let image = null

      if (currentTopicVersion && currentTopicVersion.backgroundImage) {
        const { backgroundImage } = currentTopicVersion

        const resizedImage = backgroundImage.imageVariants.find(
          (imageVariant) => imageVariant.size_key === '300x200'
        )
        image = resizedImage ? resizedImage.cdnUrl : backgroundImage?.cdnUrl
      }

      return [
        assignment.target_completion_date,
        <>
          <h4>
            <Trans>Shared by {assignment?.sharedBy?.display_name}</Trans>{' '}
            {dayjsFromNow(assignment?.share_batch_ts as string)}:
          </h4>
          <ResourceCard
            key={assignment.id}
            image={image}
            imageSrcSet={resourceCardCoverImageSrcSet(
              currentTopicVersion.backgroundImage &&
                currentTopicVersion.backgroundImage.imageVariants
            )}
            enrolment={assignment}
            topicId={topic.id}
            title={currentTopicVersion?.title}
            description={currentTopicVersion?.summary}
            author={assignment.topic.author}
            readDurationMins={currentTopicVersion?.readDurationMins}
          />
        </>
      ]
    }
  )

  const pathwayCards: DateAndElementTuples = me.assignedPathways
    .filter((pathway) => pathway.nextUnfinishedTopic) // this shouldn't be returned by the API, but since we don't always have a transaction when we calculate the pathway progress, there is very slim chance that it might be returned as null, so it's safer to guard against this
    .filter((pathway) => pathway.currentEnrolment)
    .map((pathway) => {
      const { nextUnfinishedTopic } = pathway
      const { currentTopicVersion } = nextUnfinishedTopic
      const enrolment = pathway.currentEnrolment

      return [
        enrolment.target_date,
        <>
          <h4>
            <Trans>Up next in {pathway.title}:</Trans>
          </h4>
          <ResourceCard
            key={pathway.id}
            title={currentTopicVersion.title}
            description={currentTopicVersion.summary}
            image={
              nextUnfinishedTopic.coverImage &&
              nextUnfinishedTopic.coverImage.cdnUrl
            }
            imageSrcSet={resourceCardCoverImageSrcSet(
              nextUnfinishedTopic.coverImageVariants
            )}
            author={nextUnfinishedTopic.author}
            readDurationMins={currentTopicVersion.readDurationMins}
            topicId={nextUnfinishedTopic.id}
            pathway={pathway as Partial<PathwayModelAsStudent>}
            enrolment={enrolment}
          />
        </>
      ]
    })

  const cards = [...topicAssignmentCards, ...pathwayCards]
    .sort(([dateA], [dateB]) => {
      if (dateA === null && dateB === null) {
        return 0
      }

      if (dateA === null) {
        return 1
      } else if (dateB === null) {
        return -1
      }
      // @ts-expect-error 9/11/2020
      return dateA.localeCompare(dateB)
    })
    .map((array) => array[1])

  const hiddenCount = assignedEnrolmentsCount - (topicEnrolmentLimit ?? 0)
  return (
    <PageContentWrapper className="SharedWithMe">
      <div className="grid-header">
        <h3>
          <span className="title">
            <Trans>Shared with you</Trans>
          </span>
          <TooltipHelpQuestion>
            <Trans>
              New and incomplete resources that have been shared with you will
              show up here, so it's easy to know what to do next.
            </Trans>
          </TooltipHelpQuestion>
        </h3>
      </div>
      <CardGrid
        cards={cards}
        cutOffMessage={
          topicEnrolmentLimit &&
          assignedEnrolmentsCount > topicEnrolmentLimit && (
            <div className="SharedWithMe__grid-cutoff-message">
              <Link to={staticLinks.sharedResources}>
                <Plural
                  value={hiddenCount}
                  one="# older resource share is hidden"
                  other="# older resource shares are hidden"
                />
              </Link>
            </div>
          )
        }
      />
    </PageContentWrapper>
  )
}
